import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { Prayer } from '@/types/prayer';
import { useVoiceCapture } from './useVoiceCapture';
import { PrayerAuthoringSession, prayerEditorAuth, prayerEditorIsCurrent, publishPrayerCommit } from '@/lib/prayer-authoring';
import {
  armTestimonyAttempt,
  authorPrayerAttempt,
  getLocalContentEntity,
  discardTestimonyDraft,
  discardTestimonySaveWithDraft,
  listPrayerAttempts,
  readTestimonyDraftSlot,
  rearmUndeterminedTestimonyDraft,
  withdrawLinkedTestimonyAttempt,
  withdrawUnlinkedTestimonyAttempt,
  writeTestimonyDraft,
  type ContentLifecycleAuthExpectation,
  type TestimonyDraftExpectation,
} from '@/lib/content-lifecycle/store';
import type { PrayerAttemptV1, PrayerIntent } from '@/types/prayer-command';
import type { TestimonyDraftPurpose, TestimonyDraftV1 } from '@/types/testimony-draft';
import { testimonyDraftLinkedTo, testimonyDraftSameVersion } from '@/lib/testimony-draft';
import { getCurrentContentLifecycleContext } from '@/lib/content-lifecycle/sync';
import { prayerFromCompleteDocument } from '@/lib/content-lifecycle/contracts';
import type { CompletePrayerDocument } from '@/types/content-lifecycle';
import { dispatchTestimonyDraftKeepFailed } from '@/lib/testimony-draft-events';
import { useAdiaStore } from '@/lib/store';
import type { CaptureDurableNotice } from './useSpeechToText';
import {
  audioQueueMetaDeletionAllowed,
  requiresEnhancedVoiceProcessing,
  useAudioQueueStore,
} from '@/lib/audio-queue-store';
import {
  finalizeCommittedAudio,
  recoveredAudioClaimAllowed,
  retryDestinationAudio,
  saveRefusedForUnreadableRecording,
} from '@/lib/audio-queue';
import {
  containsRecoveredVoiceText,
  mergeRecoveredVoiceText,
} from '@/lib/audio-recovery';
import type { RecoveredAudioCommitClaim } from '@/types/audio-recovery';
import { getAuthUserId } from '@/lib/keepsake/epochs';
import { enhancedVoiceAllowed } from '@/lib/core-cutover';
import { syncContentLifecycle } from '@/lib/content-lifecycle/sync';
import { useUpdateHold } from '@/hooks/useUpdateHold';

interface UseTestimonyEditOptions {
  prayer: Prayer;
  voiceTarget: string;
  onSaved?: () => void | Promise<void>;
  onCancelled?: () => void | Promise<void>;
  transformUpdatedPrayer?: (prayer: Prayer) => Prayer;
}

// Inline testimony editor shared between Evidence Book cards and (potentially)
// anywhere else that needs the same mic + textarea + submit affordance as the
// garden "add update" flow — without the Grok re-parse / gardenSummary /
// progressHistory side-effects that usePrayerUpdate carries.
//
// PrayerDetail's testimony dialog uses useSafeTestimonyEdit below instead. This
// hook (UpdateSheet's testimony variant, EvidenceBook) is deliberately left
// exactly as it was: no draft reads or writes, no exit holds, no durable
// notification handler.
export function useTestimonyEdit({
  prayer,
  voiceTarget,
  onSaved,
  onCancelled,
  transformUpdatedPrayer,
}: UseTestimonyEditOptions) {
  const [note, setNoteState] = useState(prayer.testimony ?? '');
  const [saveError, setSaveErrorState] = useState(false);
  // LC7 (review D-B): the last Save was refused because a recording it claims
  // cannot be read in this page, so "Try again" cannot work here.
  const [saveNeedsReopen, setSaveNeedsReopen] = useState(false);
  const setSaveError = useCallback((value: boolean) => {
    setSaveErrorState(value);
    setSaveNeedsReopen(false);
  }, []);
  const [isSaving, setIsSaving] = useState(false);
  // App-update safety: never reload the app while this save is in flight.
  useUpdateHold(isSaving, 'testimony-save');
  const noteRef = useRef(prayer.testimony ?? '');
  const savingRef = useRef(false);
  const voiceRecoveryTextRef = useRef('');
  const claimedRecoveryIdsRef = useRef(new Set<string>());
  const setNote = useCallback(
    (value: string | ((prev: string) => string)) => {
      const next = typeof value === 'function' ? value(noteRef.current) : value;
      noteRef.current = next;
      setNoteState(next);
    },
    [],
  );
  const voice = useVoiceCapture();
  const registerVoiceTarget = voice.registerTarget;
  const originalRef = useRef(prayer);
  const sessionRef = useRef(new PrayerAuthoringSession(prayerEditorAuth()));
  useEffect(() => {
    let canceled = false;
    void listPrayerAttempts(sessionRef.current.auth.ownerId).then(attempts => {
      const attempt = attempts.find(item => item.prayerId === prayer.id && item.intent.kind === 'testimony');
      if (canceled || !prayerEditorIsCurrent(sessionRef.current.auth) || savingRef.current || noteRef.current !== (originalRef.current.testimony ?? '') || sessionRef.current.attempt || !attempt || attempt.intent.kind !== 'testimony') return;
      sessionRef.current.attempt = attempt;
      setNote(attempt.intent.text ?? '');
    }).catch(() => {});
    return () => { canceled = true; };
  }, [prayer.id, setNote]);
  const queueItems = useAudioQueueStore((s) => s.items);
  const recoveryItems = useMemo(
    () =>
      queueItems.filter(
        (item) =>
          item.prayerId === prayer.id &&
          item.recoveryKind === 'testimony' &&
          item.ownerId === getAuthUserId() &&
          item.audioProtocolVersion === 1 &&
          item.localDisposition === 'unresolved' &&
          (item.destinationState === undefined || item.destinationState === 'unresolved'),
      ),
    [queueItems, prayer.id],
  );

  const isThisTarget = voice.target === voiceTarget;
  const isListening = voice.isRecording && isThisTarget;
  const enhancedVoiceEnabled = enhancedVoiceAllowed();
  // Park every byte-bearing recovery row while Enhanced Voice is disabled,
  // including text-ready audio. Merging a row that cannot be consumed would
  // append it again on the next mount. Intentional text/plain zero-byte
  // recovery remains claimable and can be saved normally.
  const hasPendingRecovery =
    enhancedVoiceEnabled &&
    recoveryItems.some(
      (item) => !item.failed && requiresEnhancedVoiceProcessing(item),
    );
  const hasFailedRecovery = recoveryItems.some(
    (item) => item.failed && requiresEnhancedVoiceProcessing(item),
  );
  const hasParkedRecovery =
    !enhancedVoiceEnabled &&
    recoveryItems.some(
      (item) => !audioQueueMetaDeletionAllowed(item, false),
    );
  const canRetryRecovery = enhancedVoiceEnabled && hasFailedRecovery;
  const isTranscribing =
    (voice.isTranscribing && isThisTarget) || hasPendingRecovery;
  const interim = isThisTarget ? voice.interim : '';

  useEffect(() => {
    return registerVoiceTarget(voiceTarget, {
      onFinal: (finalText, queueItemId) => {
        if (queueItemId) claimedRecoveryIdsRef.current.add(queueItemId);
        voiceRecoveryTextRef.current = mergeRecoveredVoiceText(
          voiceRecoveryTextRef.current,
          finalText,
        );
        setNote((prev) => mergeRecoveredVoiceText(prev, finalText));
      },
      getRecoveryText: () => voiceRecoveryTextRef.current,
    });
  }, [registerVoiceTarget, voiceTarget, setNote]);

  useEffect(() => {
    let cancelled = false;
    queueMicrotask(() => {
      if (cancelled) return;
      for (const item of recoveryItems) {
        const text = item.transcribedText?.trim();
        if (
          !text ||
          claimedRecoveryIdsRef.current.has(item.id) ||
          !audioQueueMetaDeletionAllowed(item, enhancedVoiceEnabled)
        ) continue;
        claimedRecoveryIdsRef.current.add(item.id);
        setNote((current) => mergeRecoveredVoiceText(current, text));
      }
    });
    return () => { cancelled = true; };
  }, [enhancedVoiceEnabled, recoveryItems, prayer.id, setNote]);

  const displayValue = interim
    ? note
      ? note + ' ' + interim
      : interim
    : note;

  const trimmedValue = displayValue.trim();
  const existingValue = prayer.testimony ?? '';
  const canSubmit = !isSaving && trimmedValue !== existingValue;

  const toggleMic = useCallback(() => {
    if (isListening) {
      if (interim) {
        setNote((prev) => (prev ? prev + ' ' + interim : interim));
      }
      void voice.stop().catch(() => {});
      return;
    }
    voiceRecoveryTextRef.current = '';
    voice.start(voiceTarget, {
      recoveryDestination: { kind: 'testimony', prayerId: prayer.id },
    });
  }, [isListening, interim, voice, voiceTarget, prayer.id, setNote]);

  const handleChange = useCallback(
    (e: React.ChangeEvent<HTMLTextAreaElement>) => {
      if (isListening || interim) void voice.stop().catch(() => {});
      setSaveError(false);
      setNote(e.target.value);
    },
    [isListening, interim, voice, setNote, setSaveError],
  );

  const commitTestimony = useCallback(async () => {
    if (savingRef.current) return;
    savingRef.current = true;
    setIsSaving(true);
    try {
      const interimAtStart = interim;
      if (isListening) await voice.stop();
      const latestNote = noteRef.current;
      const merged = interimAtStart
        ? latestNote
          ? latestNote + ' ' + interimAtStart
          : interimAtStart
        : latestNote;
      const trimmed = merged.trim();

      const original = originalRef.current;
      const transformed = transformUpdatedPrayer?.({ ...original, testimony: trimmed || null }) ?? original;
      const retained = sessionRef.current.attempt?.intent;
      const intent: Extract<PrayerIntent, { kind: 'testimony' }> = retained?.kind === 'testimony'
        ? { ...retained, text: trimmed || null }
        : { kind: 'testimony', expected: original.testimony ?? null, text: trimmed || null,
            ...(transformed.status === 'Answered' && transformUpdatedPrayer
              ? { answered: { expectedStatus: original.status, expectedManual: Boolean(original.userEditedFields?.status) } } : {}) };

      const currentRecoveryItems = useAudioQueueStore.getState().items;
      const recoveryById = new Map(currentRecoveryItems.map((item) => [item.id, item]));
      const recoveryClaims: RecoveredAudioCommitClaim[] = [];
      for (const queueItemId of claimedRecoveryIdsRef.current) {
        const item = recoveryById.get(queueItemId);
        const recovered = item?.transcribedText?.trim() ?? '';
        if (
          item?.prayerId === prayer.id &&
          item.recoveryKind === 'testimony' &&
          containsRecoveredVoiceText(trimmed, recovered) &&
          await recoveredAudioClaimAllowed(queueItemId)
        ) {
          recoveryClaims.push({ queueItemId, recoveryKind: 'testimony', expectedTranscribedText: recovered });
        }
      }

      const result = await sessionRef.current.save(prayer.id, intent, recoveryClaims);
      if (result.kind === 'committed' && !prayerEditorIsCurrent(sessionRef.current.auth)) return true;
      if (result.kind !== 'committed') { setSaveError(true); return false; }
      setSaveError(false);
      originalRef.current = result.prayer;
      sessionRef.current = new PrayerAuthoringSession(sessionRef.current.auth);
      for (const claim of result.consumedAudio) claimedRecoveryIdsRef.current.delete(claim.queueItemId);
      void syncContentLifecycle().then(async () => {
        for (const claim of result.consumedAudio) await finalizeCommittedAudio(claim.queueItemId).catch(() => false);
      }).catch(() => {});
      try { await onSaved?.(); } catch { /* durable local success survives callbacks */ }
      return true;
    } catch (error) {
      setSaveError(true);
      setSaveNeedsReopen(saveRefusedForUnreadableRecording(error));
    } finally {
      savingRef.current = false;
      setIsSaving(false);
    }
  }, [
    interim,
    isListening,
    voice,
    prayer,
    onSaved,
    transformUpdatedPrayer,
    setSaveError,
  ]);

  const retryRecovery = useCallback(async () => {
    if (!enhancedVoiceAllowed()) return;
    setSaveError(false);
    try {
      for (const item of recoveryItems.filter(
        (row) => row.failed && requiresEnhancedVoiceProcessing(row),
      )) {
        await retryDestinationAudio(item.id);
      }
    } catch {
      setSaveError(true);
    }
  }, [recoveryItems, setSaveError]);

  const cancelEdit = useCallback(async () => {
    if (savingRef.current) return;
    // Use cancel (not stop) so enhanced-mode transcription doesn't refill
    // the textarea 1–3s after the user abandons the edit.
    if (isListening) await voice.cancel();
    if (!prayerEditorIsCurrent(sessionRef.current.auth)) return;
    setSaveError(false);
    setNote(prayer.testimony ?? '');
    try {
      await onCancelled?.();
    } catch {
      setSaveError(true);
    }
  }, [isListening, voice, prayer.testimony, onCancelled, setNote, setSaveError]);

  const stopVoice = useCallback(() => {
    // Cancel, not stop — consumers call this from abandon paths (clear,
    // Escape). stop() would still POST for transcription; cancel() skips it.
    void voice.cancel();
  }, [voice]);

  return {
    note,
    displayValue,
    isListening,
    isTranscribing,
    isSupported: voice.isSupported,
    interim,
    setNote,
    toggleMic,
    handleChange,
    commitTestimony,
    cancelEdit,
    stopVoice,
    canSubmit,
    isSaving,
    saveError,
    saveNeedsReopen,
    hasFailedRecovery,
    hasParkedRecovery,
    canRetryRecovery,
    retryRecovery,
  };
}

// ---- PrayerDetail's testimony dialog: safe dismissal -------------------------
// docs/plans/testimony-safe-dismissal-2026-10-15-rev4.md, sections 4 to 8.
// Typed words live in a device-local draft (content-lifecycle/store.ts O1 to
// O9). No dismissal ever writes the prayer; only an explicit Save or "Mark
// answered without a note" does, through one testimony attempt. Recording
// follows A2 (controlled exits are held while listening), B1 (only a
// multi-entry jump drops a genuinely listening capture) and C2 (after Stop,
// exits wait only until the capture is durable on this device).

export type TestimonyDialogMode = 'capture' | 'edit';
export type TestimonyExitSource = 'outside' | 'escape' | 'button' | 'back';
/** 'one-step': one Back, the layer below is still open. 'forced': anything
 * that removes more than the dialog (a multi-entry jump, the prayer leaving,
 * a reset). */
export type TestimonyLayerCloseKind = 'one-step' | 'forced';

/** PrayerDetail's handle on the open dialog, used by the `testimony` layer. */
export interface TestimonyLayerControl {
  /** 'keep': re-register the layer and stay open. 'close': let it unmount. */
  onLayerClose: (kind: TestimonyLayerCloseKind) => 'keep' | 'close';
}

type DraftSlot = { raw: string | null; draft: TestimonyDraftV1 | null };
const EMPTY_DRAFT_SLOT: DraftSlot = { raw: null, draft: null };

/** An action this dialog shows as unfinished, or a reason it cannot act. */
export type TestimonyUnfinished =
  | { kind: 'linked'; attempt: PrayerAttemptV1; purpose: TestimonyDraftPurpose }
  | { kind: 'unlinked'; attempt: PrayerAttemptV1; purpose: TestimonyDraftPurpose }
  | { kind: 'independent' }
  | { kind: 'settings' };

// `saveReopen` (LC7, review D-B): the Save was refused because a recording it
// claims cannot be read in this page; the words stay in the draft.
export type TestimonyDialogError = 'keep' | 'save' | 'saveReopen' | 'identity' | 'recording';
export type TestimonyDialogConfirm = 'without-note' | 'discard' | 'leave' | 'remove-note';
export type TestimonyDialogConflict =
  | { kind: 'draft'; otherText: string | null; shown: boolean }
  | {
      kind: 'prayer';
      purpose: TestimonyDraftPurpose;
      savedNote: string | null;
      answered: boolean;
      /** Exactly the version shown; an explicit re-arm expects this. */
      shown: TestimonyBase;
    };

type WriteOutcome = 'durable' | 'failed' | 'stale' | 'identity';
export type TestimonyBase = { saved: string | null; status: Prayer['status']; manual: boolean };

const IDENTITY_ERRORS = new Set([
  'content-lifecycle-context-stale',
  'content-sync-owner-binding-mismatch',
  'content-sync-not-authenticated',
  'content-lifecycle-context-mismatch',
]);
const STALE_ERRORS = new Set([
  'testimony-draft-stale',
  'testimony-draft-foreign',
  'testimony-draft-linked',
  'testimony-attempt-not-linked',
  'prayer-attempt-revision-stale',
  'prayer-attempt-not-pending',
]);

function errorCode(error: unknown): string {
  return error instanceof Error ? error.message : '';
}

function classifyDraftError(error: unknown): Exclude<WriteOutcome, 'durable'> {
  const code = errorCode(error);
  if (IDENTITY_ERRORS.has(code)) return 'identity';
  if (STALE_ERRORS.has(code)) return 'stale';
  return 'failed';
}

function draftIdentity(draft: TestimonyDraftV1 | null): TestimonyDraftExpectation {
  return draft ? { draftId: draft.draftId, textRevision: draft.textRevision } : null;
}

function sameJson(left: unknown, right: unknown): boolean {
  return JSON.stringify(left ?? null) === JSON.stringify(right ?? null);
}

function attemptPurpose(attempt: PrayerAttemptV1): TestimonyDraftPurpose {
  return attempt.intent.kind === 'testimony' && attempt.intent.text === null && attempt.intent.answered
    ? 'without-note'
    : 'save';
}

/** Section 5.4: what opening finds, with no reference to the screen. */
function classifyOpen(slot: DraftSlot, attempts: PrayerAttemptV1[], saved: string | null) {
  const relevant = attempts.filter(
    (attempt) => attempt.intent.kind === 'testimony' || attempt.intent.kind === 'status',
  );
  const draft = slot.draft;
  let unfinished: TestimonyUnfinished | null = null;
  let boxText: string | null = null;
  let restored = false;
  let changedSince = false;
  let setAside: string | null = null;
  let undeterminedMatchesSaved = false;
  if (relevant.length > 1 || relevant.some((attempt) => attempt.intent.kind !== 'testimony')) {
    unfinished = { kind: 'settings' };
    boxText = draft ? draft.text : null;
  } else if (relevant.length === 1) {
    const attempt = relevant[0];
    if (draft && testimonyDraftLinkedTo(draft, attempt)) {
      unfinished = { kind: 'linked', attempt, purpose: draft.link!.purpose };
      boxText = draft.text;
    } else if (!draft) {
      // Adopted: an attempt from this build's failed action, today's code or
      // an old tab. Its text is proposed; Retry authors that same row.
      unfinished = { kind: 'unlinked', attempt, purpose: attemptPurpose(attempt) };
      boxText = attempt.intent.kind === 'testimony' ? attempt.intent.text ?? '' : null;
    } else {
      // A draft beside an attempt created independently: nothing is merged.
      unfinished = { kind: 'independent' };
      boxText = draft.text;
    }
  } else if (draft?.link) {
    // UNDETERMINED (section 5.5): never executed, never deleted automatically.
    if (draft.link.purpose === 'without-note') setAside = draft.text;
    else {
      boxText = draft.text;
      undeterminedMatchesSaved = draft.text === (saved ?? '');
    }
  } else if (draft) {
    boxText = draft.text;
    restored = true;
    changedSince = (saved ?? null) !== draft.baseTestimony;
  }
  return { unfinished, boxText, restored, changedSince, setAside, undeterminedMatchesSaved };
}

export function useSafeTestimonyEdit({
  prayer,
  mode,
  voiceTarget,
  onCommitted,
  onDismissed,
}: {
  prayer: Prayer;
  mode: TestimonyDialogMode;
  voiceTarget: string;
  /** An explicit action committed; the dialog may close. */
  onCommitted: (purpose: TestimonyDraftPurpose) => void;
  /** A safe dismissal finished: close the dialog ('back': one level only). */
  onDismissed: (via: 'in-app' | 'back') => void;
}) {
  const prayerId = prayer.id;
  const voice = useVoiceCapture();
  const registerVoiceTarget = voice.registerTarget;
  // Owner and auth epoch at open: every write carries them (section 7.2).
  const [auth] = useState<ContentLifecycleAuthExpectation>(() => prayerEditorAuth());
  const initialBase: TestimonyBase = {
    saved: prayer.testimony ?? null,
    status: prayer.status,
    manual: Boolean(prayer.userEditedFields?.status),
  };
  const [base, setBaseState] = useState<TestimonyBase>(initialBase);
  const baseRef = useRef<TestimonyBase>(initialBase);
  const setBase = useCallback((next: TestimonyBase) => {
    baseRef.current = next;
    setBaseState(next);
  }, []);

  const [note, setNoteState] = useState(prayer.testimony ?? '');
  const noteRef = useRef(prayer.testimony ?? '');
  const [phase, setPhase] = useState<'loading' | 'ready' | 'unavailable'>('loading');
  const phaseRef = useRef<'loading' | 'ready' | 'unavailable'>('loading');
  const [lookupNonce, setLookupNonce] = useState(0);
  const [slot, setSlotState] = useState<DraftSlot>(EMPTY_DRAFT_SLOT);
  const slotRef = useRef<DraftSlot>(EMPTY_DRAFT_SLOT);
  const [unfinished, setUnfinishedState] = useState<TestimonyUnfinished | null>(null);
  const unfinishedRef = useRef<TestimonyUnfinished | null>(null);
  const [restored, setRestored] = useState(false);
  const [changedSince, setChangedSince] = useState(false);
  // A set-aside note (section 5.5) is NOT the box's text: the box shows the
  // saved note, and the stored draft is kept byte for byte until an explicit
  // Use or Discard. The ref keeps it out of every dirtiness comparison.
  const [setAside, setSetAsideState] = useState<string | null>(null);
  const setAsideRef = useRef<string | null>(null);
  const adoptedAsideIdRef = useRef<string | null>(null);
  const [undeterminedMatchesSaved, setUndeterminedMatchesSaved] = useState(false);
  const [error, setError] = useState<TestimonyDialogError | null>(null);
  const [conflict, setConflictState] = useState<TestimonyDialogConflict | null>(null);
  const conflictRef = useRef<TestimonyDialogConflict | null>(null);
  const [confirm, setConfirmState] = useState<TestimonyDialogConfirm | null>(null);
  const confirmRef = useRef<TestimonyDialogConfirm | null>(null);
  const confirmShownRawRef = useRef<string | null>(null);
  const [stopFirst, setStopFirst] = useState(false);
  const [stillSaving, setStillSaving] = useState(false);
  const [busy, setBusyState] = useState(false);
  const busyRef = useRef(false);
  const busyPromiseRef = useRef<Promise<unknown> | null>(null);
  const [closing, setClosingState] = useState(false);
  const closingRef = useRef(false);
  const flushRef = useRef<Promise<WriteOutcome> | null>(null);
  const mountedRef = useRef(false);
  const dismissedRef = useRef(false);
  const abandonRef = useRef(false);
  const pendingExitRef = useRef<TestimonyExitSource | null>(null);
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const firstDirtyAtRef = useRef<number | null>(null);
  const savingLineTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const touchedRef = useRef(false);

  // Recording (sections 8.3 to 8.5).
  const captureTokenRef = useRef<string | null>(null);
  const [captureToken, setCaptureToken] = useState<string | null>(null);
  const durableTokenRef = useRef<string | null>(null);
  const [durableToken, setDurableToken] = useState<string | null>(null);
  const holdRef = useRef<string | null>(null);
  const [recordingHold, setRecordingHold] = useState(false);
  const [liveQueueId, setLiveQueueId] = useState<string | null>(null);
  const voiceRecoveryTextRef = useRef('');
  const claimedRecoveryIdsRef = useRef(new Set<string>());
  const [claimVersion, setClaimVersion] = useState(0);

  const setSlot = useCallback((next: DraftSlot) => {
    slotRef.current = next;
    setSlotState(next);
  }, []);
  const setSetAside = useCallback((next: string | null) => {
    setAsideRef.current = next;
    setSetAsideState(next);
  }, []);
  const setUnfinished = useCallback((next: TestimonyUnfinished | null) => {
    unfinishedRef.current = next;
    setUnfinishedState(next);
  }, []);
  const setConflict = useCallback((next: TestimonyDialogConflict | null) => {
    conflictRef.current = next;
    setConflictState(next);
  }, []);
  const setConfirm = useCallback((next: TestimonyDialogConfirm | null) => {
    confirmRef.current = next;
    setConfirmState(next);
  }, []);
  const setBusy = useCallback((next: boolean) => {
    busyRef.current = next;
    if (mountedRef.current) setBusyState(next);
  }, []);
  const setClosing = useCallback((next: boolean) => {
    closingRef.current = next;
    if (mountedRef.current) setClosingState(next);
  }, []);

  useEffect(() => {
    mountedRef.current = true;
    return () => { mountedRef.current = false; };
  }, []);

  /** The text that is durable for this dialog right now. */
  const storedText = useCallback((): string => {
    // While a set-aside note is offered, the box's durable baseline is the
    // saved note; the stored draft belongs to the offer, not to the box.
    if (setAsideRef.current !== null) return baseRef.current.saved ?? '';
    const draft = slotRef.current.draft;
    if (draft) return draft.text;
    const current = unfinishedRef.current;
    if (current?.kind === 'unlinked' && current.attempt.intent.kind === 'testimony') {
      return current.attempt.intent.text ?? '';
    }
    return baseRef.current.saved ?? '';
  }, []);

  // ---- Lookup at open, and re-reads after a refusal (section 5.4 / 5.2) ----
  useEffect(() => {
    let cancelled = false;
    void (async () => {
      try {
        if (!prayerEditorIsCurrent(auth)) throw new Error('content-lifecycle-context-stale');
        const [read, attempts] = await Promise.all([
          readTestimonyDraftSlot(auth.ownerId, prayerId),
          listPrayerAttempts(auth.ownerId),
        ]);
        if (cancelled || !mountedRef.current) return;
        if (read.foreign) {
          // A row the parser rejects is never touched or overwritten.
          slotRef.current = { raw: read.raw, draft: null };
          phaseRef.current = 'unavailable';
          setPhase('unavailable');
          return;
        }
        const nextSlot: DraftSlot = { raw: read.raw, draft: read.draft };
        const found = classifyOpen(
          nextSlot,
          attempts.filter((attempt) => attempt.prayerId === prayerId),
          baseRef.current.saved,
        );
        setSlot(nextSlot);
        setUnfinished(found.unfinished);
        setUndeterminedMatchesSaved(found.undeterminedMatchesSaved);
        const first = phaseRef.current === 'loading' && !touchedRef.current;
        // A note already used from the offer stays in the box; it is not
        // offered again. A set-aside appearing under typed text is a
        // two-version conflict below, never a silent swap of the box.
        const offered = found.setAside !== null && nextSlot.draft?.draftId !== adoptedAsideIdRef.current
          ? found.setAside
          : null;
        const aside = first || setAsideRef.current !== null ? offered : null;
        setSetAside(aside);
        if (first) {
          setRestored(found.restored);
          setChangedSince(found.changedSince);
          if (found.boxText !== null) {
            noteRef.current = found.boxText;
            setNoteState(found.boxText);
          }
        } else if (aside === null && nextSlot.draft && nextSlot.draft.text !== noteRef.current) {
          // Never load stored text over text on screen (section 5.2).
          setConflict({ kind: 'draft', otherText: nextSlot.draft.text, shown: false });
        }
        phaseRef.current = 'ready';
        setPhase('ready');
      } catch {
        if (cancelled || !mountedRef.current) return;
        phaseRef.current = 'unavailable';
        setPhase('unavailable');
      }
    })();
    return () => { cancelled = true; };
  }, [auth, prayerId, lookupNonce, setSlot, setUnfinished, setConflict, setSetAside]);

  const reread = useCallback(() => {
    if (mountedRef.current) setLookupNonce((value) => value + 1);
  }, []);

  // ---- O1: durable typed text (section 7) -----------------------------------
  const runWrite = useCallback(async (): Promise<WriteOutcome> => {
    // The draft slot holds an offered set-aside note: O1 never writes over it.
    if (setAsideRef.current !== null) return noteRef.current === storedText() ? 'durable' : 'failed';
    for (let pass = 0; pass < 8; pass += 1) {
      const text = noteRef.current;
      if (text === storedText()) return 'durable';
      if (!prayerEditorIsCurrent(auth)) return 'identity';
      let context;
      try {
        context = await getCurrentContentLifecycleContext(auth);
      } catch {
        return 'identity';
      }
      const current = slotRef.current;
      try {
        const next = await writeTestimonyDraft(context, {
          prayerId,
          text,
          expected: draftIdentity(current.draft),
          baseTestimony: baseRef.current.saved,
          baseStatus: baseRef.current.status,
        });
        slotRef.current = { raw: next.raw, draft: next.draft };
        if (mountedRef.current) setSlotState(slotRef.current);
      } catch (writeError) {
        return classifyDraftError(writeError);
      }
    }
    return noteRef.current === storedText() ? 'durable' : 'failed';
  }, [auth, prayerId, storedText]);

  const flushDraft = useCallback((): Promise<WriteOutcome> => {
    if (flushRef.current) return flushRef.current;
    if (savingLineTimerRef.current) clearTimeout(savingLineTimerRef.current);
    savingLineTimerRef.current = setTimeout(() => {
      if (mountedRef.current) setStillSaving(true);
    }, 1200);
    const tracked = runWrite()
      .then((outcome) => {
        if (mountedRef.current) {
          if (outcome === 'failed') setError('keep');
          else if (outcome === 'identity') setError('identity');
          else if (outcome === 'stale') reread();
          else setError((current) => (current === 'keep' ? null : current));
        }
        return outcome;
      })
      .finally(() => {
        if (flushRef.current === tracked) flushRef.current = null;
        if (savingLineTimerRef.current) clearTimeout(savingLineTimerRef.current);
        savingLineTimerRef.current = null;
        if (mountedRef.current) setStillSaving(false);
      });
    flushRef.current = tracked;
    return tracked;
  }, [runWrite, reread]);

  const canAutosave = useCallback(() => (
    phaseRef.current === 'ready' &&
    !busyRef.current &&
    !conflictRef.current &&
    prayerEditorIsCurrent(auth)
  ), [auth]);

  // 800 ms after the last change, and at least every 5 s while typing.
  const scheduleAutosave = useCallback(() => {
    const now = Date.now();
    if (firstDirtyAtRef.current === null) firstDirtyAtRef.current = now;
    if (debounceRef.current) clearTimeout(debounceRef.current);
    const wait = Math.max(0, Math.min(800, 5000 - (now - firstDirtyAtRef.current)));
    debounceRef.current = setTimeout(() => {
      debounceRef.current = null;
      firstDirtyAtRef.current = null;
      if (canAutosave()) void flushDraft();
    }, wait);
  }, [canAutosave, flushDraft]);

  const applyNote = useCallback((value: string | ((prev: string) => string), autosave = true) => {
    const next = typeof value === 'function' ? value(noteRef.current) : value;
    noteRef.current = next;
    touchedRef.current = true;
    setNoteState(next);
    if (autosave) scheduleAutosave();
  }, [scheduleAutosave]);

  // Hidden page or pagehide: best-effort write.
  useEffect(() => {
    const persist = () => {
      if (canAutosave()) void flushDraft();
    };
    const onVisibility = () => {
      if (document.visibilityState === 'hidden') persist();
    };
    document.addEventListener('visibilitychange', onVisibility);
    window.addEventListener('pagehide', persist);
    return () => {
      document.removeEventListener('visibilitychange', onVisibility);
      window.removeEventListener('pagehide', persist);
    };
  }, [canAutosave, flushDraft]);

  // Forced teardown (section 7.2): one final write with the text at unmount.
  // It needs no component; if it fails or stalls, the app-level notice says
  // the latest changes could not be kept (bound to this owner and epoch).
  useEffect(() => () => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    if (savingLineTimerRef.current) clearTimeout(savingLineTimerRef.current);
    const issued = auth;
    const finalWrite = async () => {
      await busyPromiseRef.current?.catch(() => undefined);
      if (abandonRef.current) return;
      if (noteRef.current === storedText()) return;
      // An unresolved two-version conflict is never overwritten silently.
      const outcome: WriteOutcome = conflictRef.current
        ? 'stale'
        : await (flushRef.current ?? runWrite());
      if (outcome !== 'durable' && noteRef.current !== storedText()) {
        dispatchTestimonyDraftKeepFailed({
          ownerId: issued.ownerId,
          authEpoch: issued.authEpoch,
          failureId: crypto.randomUUID(),
        });
      }
    };
    void finalWrite();
  }, [auth, runWrite, storedText]);

  // ---- Recovered recordings: explicit offers (section 8.1) ------------------
  const queueItems = useAudioQueueStore((s) => s.items);
  const recoveryItems = useMemo(
    () =>
      queueItems.filter(
        (item) =>
          item.prayerId === prayerId &&
          item.recoveryKind === 'testimony' &&
          item.ownerId === auth.ownerId &&
          item.audioProtocolVersion === 1 &&
          item.localDisposition === 'unresolved' &&
          (item.destinationState === undefined || item.destinationState === 'unresolved'),
      ),
    [queueItems, prayerId, auth.ownerId],
  );
  const enhancedVoiceEnabled = enhancedVoiceAllowed();
  const isThisTarget = voice.target === voiceTarget;
  const isListening = voice.isRecording && isThisTarget;
  const hasPendingRecovery =
    enhancedVoiceEnabled &&
    recoveryItems.some((item) => !item.failed && requiresEnhancedVoiceProcessing(item));
  const hasFailedRecovery = recoveryItems.some(
    (item) => item.failed && requiresEnhancedVoiceProcessing(item),
  );
  const hasParkedRecovery =
    !enhancedVoiceEnabled &&
    recoveryItems.some((item) => !audioQueueMetaDeletionAllowed(item, false));
  const canRetryRecovery = enhancedVoiceEnabled && hasFailedRecovery;
  const isTranscribing = (voice.isTranscribing && isThisTarget) || hasPendingRecovery;
  const interim = isThisTarget ? voice.interim : '';

  // A transcript the box already contains is claimed for the save (its
  // consumption is re-checked inside the save transaction); one it does not
  // contain is offered, never appended automatically.
  useEffect(() => {
    let cancelled = false;
    queueMicrotask(() => {
      if (cancelled) return;
      let changed = false;
      for (const item of recoveryItems) {
        const text = item.transcribedText?.trim();
        if (!text || claimedRecoveryIdsRef.current.has(item.id)) continue;
        if (!audioQueueMetaDeletionAllowed(item, enhancedVoiceEnabled)) continue;
        if (containsRecoveredVoiceText(noteRef.current, text)) {
          claimedRecoveryIdsRef.current.add(item.id);
          changed = true;
        }
      }
      if (changed) setClaimVersion((value) => value + 1);
    });
    return () => { cancelled = true; };
  }, [recoveryItems, enhancedVoiceEnabled, note]);

  const recordingOffers = useMemo(() => {
    void claimVersion;
    return recoveryItems.filter((item) => {
      const text = item.transcribedText?.trim();
      return Boolean(text) &&
        item.id !== liveQueueId &&
        !claimedRecoveryIdsRef.current.has(item.id) &&
        audioQueueMetaDeletionAllowed(item, enhancedVoiceEnabled) &&
        !containsRecoveredVoiceText(note, text!);
    });
  }, [recoveryItems, enhancedVoiceEnabled, note, claimVersion, liveQueueId]);

  const addRecordingWords = useCallback((itemId: string) => {
    const item = useAudioQueueStore.getState().items.find((row) => row.id === itemId);
    const text = item?.transcribedText?.trim();
    if (!text || busyRef.current || closingRef.current) return;
    claimedRecoveryIdsRef.current.add(itemId);
    setClaimVersion((value) => value + 1);
    applyNote((prev) => mergeRecoveredVoiceText(prev, text));
  }, [applyNote]);

  const retryRecovery = useCallback(async () => {
    if (!enhancedVoiceAllowed()) return;
    try {
      for (const item of recoveryItems.filter(
        (row) => row.failed && requiresEnhancedVoiceProcessing(row),
      )) {
        await retryDestinationAudio(item.id);
      }
    } catch {
      if (mountedRef.current) setError('save');
    }
  }, [recoveryItems]);

  // ---- The live capture ------------------------------------------------------
  const releaseHold = useCallback((token: string | null) => {
    if (!mountedRef.current || !token || holdRef.current !== token) return;
    holdRef.current = null;
    setRecordingHold(false);
  }, []);

  useEffect(() => {
    return registerVoiceTarget(voiceTarget, {
      onFinal: (finalText, queueItemId) => {
        if (queueItemId) claimedRecoveryIdsRef.current.add(queueItemId);
        voiceRecoveryTextRef.current = mergeRecoveredVoiceText(
          voiceRecoveryTextRef.current,
          finalText,
        );
        applyNote((prev) => mergeRecoveredVoiceText(prev, finalText));
      },
      getRecoveryText: () => voiceRecoveryTextRef.current,
      // C2: constant-time, synchronous: compare, record, set one flag.
      onCaptureDurable: (notice: CaptureDurableNotice) => {
        const token = captureTokenRef.current;
        if (
          !token ||
          notice.captureToken !== token ||
          notice.ownerId !== auth.ownerId ||
          notice.authEpoch !== auth.authEpoch ||
          notice.recoveryDestination?.kind !== 'testimony' ||
          notice.recoveryDestination.prayerId !== prayerId
        ) return;
        setLiveQueueId(notice.queueId);
        durableTokenRef.current = token;
        setDurableToken(token);
        if (holdRef.current === token) {
          holdRef.current = null;
          setRecordingHold(false);
        }
      },
    });
  }, [registerVoiceTarget, voiceTarget, applyNote, auth, prayerId]);

  // A stop the dialog did not request (the 10-minute cap, an interruption)
  // also holds exits until its capture is durable or the stop settles.
  const unrequestedHold =
    voice.isStopping && isThisTarget && captureToken !== null && durableToken !== captureToken;
  const holding = recordingHold || unrequestedHold;

  const isHolding = useCallback((): boolean => {
    if (holdRef.current) return true;
    const capture = voice.getCaptureState();
    return capture.target === voiceTarget &&
      capture.stopping &&
      capture.captureToken !== undefined &&
      capture.captureToken !== durableTokenRef.current;
  }, [voice, voiceTarget]);

  const requestStop = useCallback(() => {
    const token = captureTokenRef.current;
    if (token && durableTokenRef.current !== token) {
      holdRef.current = token;
      setRecordingHold(true);
    }
    // The promise is kept (it is discarded in the shared editor) and guarded
    // after unmount: a settlement then sets no state.
    voice.stop().then(
      () => releaseHold(token),
      () => {
        if (mountedRef.current) setError('recording');
        releaseHold(token);
      },
    );
  }, [voice, releaseHold]);

  const authCurrent = prayerEditorIsCurrent(auth);
  const editable =
    phase === 'ready' &&
    authCurrent &&
    !busy &&
    !closing &&
    confirm === null &&
    setAside === null;

  const toggleMic = useCallback(() => {
    if (isListening) {
      if (interim) applyNote((prev) => (prev ? prev + ' ' + interim : interim));
      requestStop();
      return;
    }
    if (!editable || holding) return;
    const token = crypto.randomUUID();
    voiceRecoveryTextRef.current = '';
    captureTokenRef.current = token;
    setCaptureToken(token);
    setStopFirst(false);
    setError((current) => (current === 'recording' ? null : current));
    const accepted = voice.start(voiceTarget, {
      recoveryDestination: { kind: 'testimony', prayerId },
      captureToken: token,
    });
    if (!accepted) {
      captureTokenRef.current = null;
      setCaptureToken(null);
    }
  }, [isListening, interim, applyNote, requestStop, editable, holding, voice, voiceTarget, prayerId]);

  const handleChange = useCallback((event: React.ChangeEvent<HTMLTextAreaElement>) => {
    if (!editable) return;
    if (isListening || interim) requestStop();
    if (error === 'keep' || error === 'save' || error === 'saveReopen') setError(null);
    applyNote(event.target.value);
  }, [editable, isListening, interim, requestStop, error, applyNote]);

  const displayValue = interim ? (note ? note + ' ' + interim : interim) : note;

  // ---- Explicit operations ---------------------------------------------------
  const runExclusive = useCallback(async <T,>(work: () => Promise<T>): Promise<T | undefined> => {
    if (busyRef.current || closingRef.current) return undefined;
    setBusy(true);
    const tracked = (async () => {
      // Let a write already in flight settle; no separate O1 is started here.
      if (flushRef.current) await flushRef.current.catch(() => 'failed');
      return work();
    })();
    busyPromiseRef.current = tracked;
    try {
      return await tracked;
    } finally {
      if (busyPromiseRef.current === tracked) busyPromiseRef.current = null;
      setBusy(false);
    }
  }, [setBusy]);

  // The prayer as this device durably holds it (the in-memory store can lag
  // behind another tab's commit).
  const readCurrentPrayer = useCallback(async (): Promise<Prayer | null> => {
    try {
      const entity = await getLocalContentEntity(auth.ownerId, 'prayer', prayerId);
      if (entity?.visibleDocument) {
        return prayerFromCompleteDocument(entity.visibleDocument as CompletePrayerDocument);
      }
    } catch {
      // Fall back to the store below.
    }
    return useAdiaStore.getState().prayers.find((row) => row.id === prayerId) ?? null;
  }, [auth.ownerId, prayerId]);

  const handleOperationError = useCallback((operationError: unknown, purpose?: TestimonyDraftPurpose) => {
    if (!mountedRef.current) return;
    const code = errorCode(operationError);
    if (operationError instanceof Error && operationError.name === 'PrayerIntentConflictError' && purpose) {
      void readCurrentPrayer().then((current) => {
        if (!mountedRef.current) return;
        const shown: TestimonyBase = {
          saved: current?.testimony ?? null,
          status: current?.status ?? baseRef.current.status,
          manual: Boolean(current?.userEditedFields?.status),
        };
        setConflict({
          kind: 'prayer',
          purpose,
          savedNote: shown.saved,
          answered: shown.status === 'Answered',
          shown,
        });
      });
      reread();
      return;
    }
    if (code === 'testimony-attempt-independent') {
      setUnfinished({ kind: 'independent' });
      return;
    }
    if (IDENTITY_ERRORS.has(code)) {
      setError('identity');
      return;
    }
    if (STALE_ERRORS.has(code)) {
      reread();
      return;
    }
    if (purpose && saveRefusedForUnreadableRecording(operationError)) {
      setError('saveReopen');
      return;
    }
    setError(purpose ? 'save' : 'keep');
  }, [readCurrentPrayer, reread, setConflict, setUnfinished]);

  const recoveryClaimsFor = useCallback(async (text: string) => {
    const byId = new Map(useAudioQueueStore.getState().items.map((item) => [item.id, item]));
    const claims: RecoveredAudioCommitClaim[] = [];
    for (const queueItemId of claimedRecoveryIdsRef.current) {
      const item = byId.get(queueItemId);
      const recovered = item?.transcribedText?.trim() ?? '';
      if (
        item?.prayerId === prayerId &&
        item.recoveryKind === 'testimony' &&
        containsRecoveredVoiceText(text, recovered) &&
        await recoveredAudioClaimAllowed(queueItemId)
      ) {
        claims.push({ queueItemId, recoveryKind: 'testimony', expectedTranscribedText: recovered });
      }
    }
    return claims;
  }, [prayerId]);

  /** O2 then O3 (sections 4 and 6.1). Never a status-only intent. */
  const commit = useCallback((purpose: TestimonyDraftPurpose) => runExclusive(async () => {
    if (!prayerEditorIsCurrent(auth)) {
      if (mountedRef.current) setError('identity');
      return;
    }
    if (mountedRef.current) {
      setError(null);
      setConflict(null);
      setConfirm(null);
    }
    try {
      const context = await getCurrentContentLifecycleContext(auth);
      const box = noteRef.current;
      const trimmed = box.trim();
      const current = baseRef.current;
      const answered = { expectedStatus: current.status, expectedManual: current.manual };
      const intent: Extract<PrayerIntent, { kind: 'testimony' }> = purpose === 'save'
        ? { kind: 'testimony', expected: current.saved, text: trimmed || null,
            ...(mode === 'capture' ? { answered } : {}) }
        : { kind: 'testimony', expected: current.saved, text: null, answered };
      const claims = purpose === 'save' ? await recoveryClaimsFor(trimmed) : [];
      const shown = unfinishedRef.current;
      let attempt = shown && (shown.kind === 'linked' || shown.kind === 'unlinked')
        ? shown.attempt
        : null;
      const currentSlot = slotRef.current;
      const sameLinkedRetry = Boolean(
        attempt && currentSlot.draft &&
        testimonyDraftLinkedTo(currentSlot.draft, attempt) &&
        testimonyDraftSameVersion(currentSlot.draft) &&
        currentSlot.draft.link?.purpose === purpose &&
        currentSlot.draft.text === box &&
        sameJson(attempt.intent, intent) &&
        sameJson(attempt.recoveredAudio, claims),
      );
      const sameUnlinkedRetry = Boolean(
        attempt && !currentSlot.draft &&
        box === storedText() &&
        sameJson(attempt.intent, intent) &&
        sameJson(attempt.recoveredAudio, claims),
      );
      if (!sameLinkedRetry && !sameUnlinkedRetry) {
        // An UNDETERMINED draft (section 5.5) cannot be re-linked by O2; O10
        // re-arms exactly the row shown in one transaction, or changes nothing.
        const armed = !attempt && currentSlot.draft?.link
          ? await rearmUndeterminedTestimonyDraft(context, {
              prayerId,
              shownDraftRaw: currentSlot.raw ?? '',
              intent,
              text: box,
              purpose,
              recoveredAudio: claims,
            })
          : await armTestimonyAttempt(context, {
              prayerId,
              intent,
              text: box,
              purpose,
              savedTestimony: current.saved,
              baseStatus: current.status,
              expectedDraft: draftIdentity(currentSlot.draft),
              previousAttempt: attempt,
              recoveredAudio: claims,
            });
        attempt = armed.attempt;
        const nextSlot = armed.draft
          ? { raw: armed.draft.raw, draft: armed.draft.draft }
          : EMPTY_DRAFT_SLOT;
        setSlot(nextSlot);
        setUnfinished(nextSlot.draft
          ? { kind: 'linked', attempt, purpose }
          : { kind: 'unlinked', attempt, purpose });
      }
      if (!attempt) return;
      const result = await authorPrayerAttempt(context, attempt);
      if (result.kind !== 'committed') {
        // `not-pending` is never success: re-read and show what is stored.
        reread();
        return;
      }
      abandonRef.current = true;
      dismissedRef.current = true;
      publishPrayerCommit(result);
      for (const claim of result.consumedAudio) claimedRecoveryIdsRef.current.delete(claim.queueItemId);
      void syncContentLifecycle().then(async () => {
        for (const claim of result.consumedAudio) await finalizeCommittedAudio(claim.queueItemId).catch(() => false);
      }).catch(() => {});
      if (!mountedRef.current || !prayerEditorIsCurrent(auth)) return;
      setSlot(EMPTY_DRAFT_SLOT);
      setUnfinished(null);
      onCommitted(purpose);
    } catch (operationError) {
      handleOperationError(operationError, purpose);
    }
  }), [runExclusive, auth, mode, recoveryClaimsFor, storedText, prayerId, setSlot, setUnfinished, reread, onCommitted, handleOperationError, setConflict, setConfirm]);

  /** O4 or O9, keeping the box text as an ordinary draft (section 3.4, 6.2). */
  const withdrawShown = useCallback(async (): Promise<boolean> => {
    const shown = unfinishedRef.current;
    if (!shown || (shown.kind !== 'linked' && shown.kind !== 'unlinked')) return true;
    const context = await getCurrentContentLifecycleContext(auth);
    const currentSlot = slotRef.current;
    const kept = shown.kind === 'linked'
      ? await withdrawLinkedTestimonyAttempt(context, {
          attempt: shown.attempt,
          expectedDraft: draftIdentity(currentSlot.draft)!,
          text: noteRef.current,
        })
      : await withdrawUnlinkedTestimonyAttempt(context, {
          attempt: shown.attempt,
          expectedDraftRaw: currentSlot.raw,
          text: noteRef.current,
          savedTestimony: baseRef.current.saved,
          baseStatus: baseRef.current.status,
        });
    setSlot(kept ? { raw: kept.raw, draft: kept.draft } : EMPTY_DRAFT_SLOT);
    setUnfinished(null);
    return true;
  }, [auth, setSlot, setUnfinished]);

  /** "Keep my note": withdraw the unfinished action, stay open (section 4). */
  const keepMyNote = useCallback(() => runExclusive(async () => {
    try {
      await withdrawShown();
      if (mountedRef.current) {
        setConflict(null);
        setConfirm(null);
        setError(null);
      }
    } catch (operationError) {
      handleOperationError(operationError);
    }
  }), [runExclusive, withdrawShown, setConflict, setConfirm, handleOperationError]);

  // ---- Safe dismissal (section 6.2) -------------------------------------------
  const requestDismiss = useCallback(async (source: TestimonyExitSource): Promise<boolean> => {
    if (dismissedRef.current) return true;
    if (busyRef.current || closingRef.current) return false;
    if (confirmRef.current) {
      // Escape, an outside tap or Back inside a confirmation returns to editing.
      setConfirm(null);
      return false;
    }
    const capture = voice.getCaptureState();
    if (capture.target === voiceTarget && capture.listening) {
      setStopFirst(true);
      return false;
    }
    if (isHolding()) {
      pendingExitRef.current = source;
      return false;
    }
    setClosing(true);
    try {
      if (flushRef.current) await flushRef.current;
      if (conflictRef.current?.kind === 'draft') return false;
      const nothingTyped = noteRef.current === (baseRef.current.saved ?? '');
      const shown = unfinishedRef.current;
      if (phaseRef.current !== 'ready') {
        // Nothing loaded, nothing typed (the box was read-only): close.
        if (noteRef.current !== storedText()) return false;
      } else if (shown && (shown.kind === 'linked' || shown.kind === 'unlinked')) {
        if (!prayerEditorIsCurrent(auth)) { setError('identity'); return false; }
        await withdrawShown();
      } else if (nothingTyped) {
        const draft = slotRef.current.draft;
        // Only this dialog's own unlinked draft is removed; an UNDETERMINED or
        // set-aside draft is never deleted automatically.
        if (draft && !draft.link && setAsideRef.current === null) {
          if (!prayerEditorIsCurrent(auth)) { setError('identity'); return false; }
          const context = await getCurrentContentLifecycleContext(auth);
          await discardTestimonyDraft(context, { prayerId, shownRaw: slotRef.current.raw ?? '' });
          setSlot(EMPTY_DRAFT_SLOT);
        }
      } else {
        let outcome = await flushDraft();
        if (outcome === 'durable' && noteRef.current !== storedText()) outcome = await flushDraft();
        if (outcome !== 'durable') return false;
      }
      dismissedRef.current = true;
      if (mountedRef.current) onDismissed(source === 'back' ? 'back' : 'in-app');
      return true;
    } catch (operationError) {
      handleOperationError(operationError);
      return false;
    } finally {
      setClosing(false);
    }
  }, [voice, voiceTarget, isHolding, setClosing, setConfirm, storedText, auth, withdrawShown, prayerId, setSlot, flushDraft, onDismissed, handleOperationError]);

  // Resume an exit that waited on a recording hold once the hold releases.
  useEffect(() => {
    if (holding || pendingExitRef.current === null) return;
    const timer = setTimeout(() => {
      const source = pendingExitRef.current;
      pendingExitRef.current = null;
      if (source) void requestDismiss(source);
    }, 0);
    return () => clearTimeout(timer);
  }, [holding, requestDismiss]);

  const canCloseNow = useCallback((): boolean => {
    if (busyRef.current || closingRef.current || flushRef.current || confirmRef.current) return false;
    if (conflictRef.current || isHolding()) return false;
    const capture = voice.getCaptureState();
    if (capture.target === voiceTarget && capture.listening) return false;
    const shown = unfinishedRef.current;
    if (shown && (shown.kind === 'linked' || shown.kind === 'unlinked')) return false;
    if (noteRef.current !== storedText()) return false;
    const draft = slotRef.current.draft;
    return !(draft && !draft.link && noteRef.current === (baseRef.current.saved ?? ''));
  }, [isHolding, voice, voiceTarget, storedText]);

  /** The `testimony` layer close (section 6.3, 8.4). */
  const onLayerClose = useCallback((kind: TestimonyLayerCloseKind): 'keep' | 'close' => {
    if (dismissedRef.current) return 'close';
    if (kind === 'forced') {
      // B1: only a genuinely listening capture of this dialog is dropped. A
      // stop underway finishes as started; the unmount write keeps the text.
      const capture = voice.getCaptureState();
      if (capture.target === voiceTarget && capture.listening && !capture.stopping) {
        void voice.cancel();
      }
      return 'close';
    }
    if (canCloseNow()) {
      dismissedRef.current = true;
      return 'close';
    }
    void requestDismiss('back');
    return 'keep';
  }, [voice, voiceTarget, canCloseNow, requestDismiss]);

  const layerControl = useMemo<TestimonyLayerControl>(() => ({ onLayerClose }), [onLayerClose]);

  // ---- Small explicit actions ----------------------------------------------------
  const markWithoutNote = useCallback(() => {
    if (!editable || holding) return;
    if (baseRef.current.saved === null && noteRef.current.trim() !== '') {
      setConfirm('without-note');
      return;
    }
    void commit('without-note');
  }, [editable, holding, setConfirm, commit]);

  const openDiscardConfirm = useCallback(async () => {
    if (!editable || holding) return;
    if (flushRef.current) await flushRef.current;
    confirmShownRawRef.current = slotRef.current.raw;
    setConfirm('discard');
  }, [editable, holding, setConfirm]);

  /** O5, or O8 while an unfinished save is shown (section 5.1). */
  const discardDraft = useCallback(() => runExclusive(async () => {
    try {
      const shown = unfinishedRef.current;
      const shownRaw = confirmShownRawRef.current;
      if (shownRaw !== null) {
        const context = await getCurrentContentLifecycleContext(auth);
        if (shown?.kind === 'linked' && shown.purpose === 'save') {
          await discardTestimonySaveWithDraft(context, { attempt: shown.attempt, shownDraftRaw: shownRaw });
          setUnfinished(null);
        } else if (!shown || shown.kind === 'independent' || shown.kind === 'settings') {
          await discardTestimonyDraft(context, { prayerId, shownRaw });
        }
      }
      setSlot(EMPTY_DRAFT_SLOT);
      if (mountedRef.current) {
        setConfirm(null);
        setRestored(false);
        setChangedSince(false);
        setError(null);
      }
      applyNote(baseRef.current.saved ?? '', false);
    } catch (operationError) {
      if (mountedRef.current) setConfirm(null);
      handleOperationError(operationError);
    }
  }), [runExclusive, auth, prayerId, setUnfinished, setSlot, setConfirm, applyNote, handleOperationError]);

  /** "Leave without keeping", after a write failed (section 6.2 item 8). */
  const leaveWithoutKeeping = useCallback(() => {
    abandonRef.current = true;
    dismissedRef.current = true;
    setConfirm(null);
    onDismissed('in-app');
  }, [setConfirm, onDismissed]);

  /** "Keep what is on this screen": an explicit O1 against the identity just read. */
  const keepOnScreen = useCallback(async () => {
    if (conflictRef.current?.kind !== 'draft') return;
    setConflict(null);
    const outcome = await flushDraft();
    if (outcome === 'stale' && mountedRef.current) reread();
  }, [setConflict, flushDraft, reread]);

  const showOtherVersion = useCallback(() => {
    const current = conflictRef.current;
    if (current?.kind === 'draft') setConflict({ ...current, shown: true });
  }, [setConflict]);

  /** Re-arm T at its next revision against exactly the version just shown,
   * after an explicit confirmation (section 4). */
  const rearmAgainstCurrent = useCallback((purpose: TestimonyDraftPurpose) => {
    const shown = conflictRef.current;
    if (shown?.kind !== 'prayer') return;
    setBase(shown.shown);
    void commit(purpose);
  }, [setBase, commit]);

  /** "Use": the offered note becomes the box text (already durable as is). */
  const adoptSetAside = useCallback(() => {
    const text = setAsideRef.current;
    if (text === null || busyRef.current || closingRef.current) return;
    adoptedAsideIdRef.current = slotRef.current.draft?.draftId ?? null;
    setSetAside(null);
    applyNote(text);
  }, [setSetAside, applyNote]);

  const discardSetAside = useCallback(() => runExclusive(async () => {
    try {
      const context = await getCurrentContentLifecycleContext(auth);
      await discardTestimonyDraft(context, { prayerId, shownRaw: slotRef.current.raw ?? '' });
      setSlot(EMPTY_DRAFT_SLOT);
      setSetAside(null);
    } catch (operationError) {
      handleOperationError(operationError);
    }
  }), [runExclusive, auth, prayerId, setSlot, setSetAside, handleOperationError]);

  const retryKeep = useCallback(() => {
    setError(null);
    void flushDraft();
  }, [flushDraft]);

  const retryLookup = useCallback(() => {
    phaseRef.current = 'loading';
    setPhase('loading');
    reread();
  }, [reread]);

  const saved = base.saved ?? '';
  const canSave =
    editable &&
    !holding &&
    !isListening &&
    unfinished?.kind !== 'independent' &&
    unfinished?.kind !== 'settings' &&
    conflict === null &&
    (mode === 'capture' ? note.trim() !== '' : note.trim() !== saved);
  const showWithoutNote =
    mode === 'capture' &&
    unfinished?.kind !== 'independent' &&
    unfinished?.kind !== 'settings' &&
    (base.saved === null || note.trim() === '');
  const unfinishedAction =
    unfinished && (unfinished.kind === 'linked' || unfinished.kind === 'unlinked')
      ? unfinished
      : null;
  const canDiscardDraft =
    editable &&
    (!unfinishedAction || (unfinishedAction.kind === 'linked' && unfinishedAction.purpose === 'save')) &&
    (slot.draft !== null || note !== saved);

  return {
    mode,
    note,
    displayValue,
    phase,
    editable,
    authCurrent,
    busy,
    closing,
    holding,
    isListening,
    isTranscribing,
    isSupported: voice.isSupported,
    interim,
    stopFirst: stopFirst && isListening,
    stillSaving,
    error,
    conflict,
    confirm,
    restored,
    changedSince,
    setAside,
    undeterminedMatchesSaved,
    unfinished,
    unfinishedAction,
    savedNote: base.saved,
    canSave,
    showWithoutNote,
    canDiscardDraft,
    recordingOffers,
    hasFailedRecovery,
    hasParkedRecovery,
    canRetryRecovery,
    layerControl,
    handleChange,
    toggleMic,
    requestDismiss,
    save: () => commit('save'),
    markWithoutNote,
    confirmWithoutNote: () => commit('without-note'),
    keepMyNote,
    rearmAgainstCurrent,
    openDiscardConfirm,
    discardDraft,
    cancelConfirm: () => setConfirm(null),
    askLeaveWithoutKeeping: () => setConfirm('leave'),
    askRemoveNote: () => setConfirm('remove-note'),
    leaveWithoutKeeping,
    keepOnScreen,
    showOtherVersion,
    adoptSetAside,
    discardSetAside,
    addRecordingWords,
    retryRecovery,
    retryKeep,
    retryLookup,
  };
}
